import { useState, type FormEvent } from "react";
import { useAuthContext } from "@/components/AuthProvider";
import { useUpgradeToOwner } from "@/hooks/useUpgradeToOwner";
import type { PendingUpgradeUser } from "@/hooks/useAuth";

/**
 * 강사 → 원장 전환 화면
 *
 * admin-web에 강사 계정으로 로그인하면(useAuth가 needs-upgrade 상태로 분기) 강제
 * 로그아웃 대신 이 화면이 렌더된다. 초대 코드를 입력해 본인 계정을 owner로 전환하고,
 * 성공 시 프로필을 재조회(refresh)하여 그대로 원장 대시보드로 진입한다.
 */
export function UpgradeToOwnerPage({ pending }: { pending: PendingUpgradeUser }) {
  const { refresh, signOut } = useAuthContext();
  const upgrade = useUpgradeToOwner();
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await upgrade.mutateAsync({ inviteCode: inviteCode.trim() });
      // role=owner 로 갱신된 프로필 재조회 → needs-upgrade → authenticated
      await refresh();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow">
        <div>
          <h2 className="text-center text-3xl font-bold text-gray-900">원장으로 전환</h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {pending.name}님은 강사 계정으로 로그인되어 있습니다.
            <br />
            초대 코드를 입력하면 원장 계정으로 전환됩니다.
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">{error}</div>}
          <div>
            <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-700">
              초대 코드
            </label>
            <input
              id="inviteCode"
              type="text"
              required
              autoComplete="off"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={upgrade.isPending || inviteCode.trim().length === 0}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {upgrade.isPending ? "전환 중..." : "원장으로 전환"}
          </button>
          <button
            type="button"
            onClick={() => void signOut()}
            className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
          >
            다른 계정으로 로그인
          </button>
        </form>
      </div>
    </div>
  );
}

/** Cloud Function(HttpsError)의 한글 메시지를 그대로 노출한다. */
function getErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return "원장 전환에 실패했습니다. 잠시 후 다시 시도해 주세요.";
}
